/**
 * Native time input helper for `.timepicker` fields.
 *
 * This helper keeps the value/data-time normalization contract on native
 * `<input type="time">` controls.
 */
(function (root) {
    var DATA_KEY = '__skmTimeInputNative';

    var defaults = {
        timeFormat: 'hh:mm:ss.000',
        format: 'h:mm tt',
        theme: 'red',
        readOnly: true,
        hourPadding: false,
    };

    function pad(value) {
        value = parseInt(value, 10);
        return value < 10 ? '0' + value : String(value);
    }

    function parseTime(value) {
        if (value === null || typeof value === 'undefined') return null;

        var raw = String(value).trim();
        if (!raw) return null;

        var match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?\s*(AM|PM|am|pm)?$/);
        if (!match) return null;

        var hour = parseInt(match[1], 10);
        var minute = parseInt(match[2], 10);
        var second = typeof match[3] === 'undefined' ? 0 : parseInt(match[3], 10);
        var meridiem = match[4] ? match[4].toUpperCase() : null;

        if (minute > 59 || second > 59) return null;

        if (meridiem) {
            if (hour < 1 || hour > 12) return null;
            if (meridiem === 'PM' && hour < 12) hour += 12;
            if (meridiem === 'AM' && hour === 12) hour = 0;
        } else if (hour > 23) {
            return null;
        }

        return {hour: hour, minute: minute, second: second};
    }

    function formatTime(time, format, hourPadding) {
        var is24Hour = (format.match(/h/g) || []).length > 1;
        var displayHour = is24Hour
            ? time.hour
            : (time.hour === 0 || time.hour === 12 ? 12 : time.hour % 12);
        var replacements = {
            hh: pad(time.hour),
            h: hourPadding && displayHour < 10 ? pad(displayHour) : String(displayHour),
            mm: pad(time.minute),
            ss: pad(time.second),
            tt: is24Hour ? '' : (time.hour < 12 ? 'AM' : 'PM'),
            t: is24Hour ? '' : (time.hour < 12 ? 'am' : 'pm'),
        };

        return format.replace(/hh|h|mm|ss|tt|t/g, function (token) {
            return replacements[token];
        }).trim();
    }

    function nativeValue(time, includeSeconds) {
        return pad(time.hour) + ':' + pad(time.minute) + (includeSeconds ? ':' + pad(time.second) : '');
    }

    function supportsNativeTimeInput() {
        if (!root.document) return false;

        var testInput = root.document.createElement('input');
        testInput.setAttribute('type', 'time');

        return testInput.type === 'time';
    }

    function updateDataAttributes(input, options, shouldEmit) {
        var time = parseTime(input.value);

        if (!time) {
            input.removeAttribute('data-time');
            input.setAttribute('value', input.value || '');
            return;
        }

        var dataTime = formatTime(time, options.timeFormat, false);
        input.setAttribute('data-time', dataTime);
        input.setAttribute('value', input.value);

        if (shouldEmit && typeof root.CustomEvent === 'function') {
            input.dispatchEvent(new root.CustomEvent('timechanged', {
                bubbles: true,
                detail: {
                    time: dataTime,
                    value: input.value,
                    formattedValue: formatTime(time, options.format, options.hourPadding),
                },
            }));
        }
    }

    function initInput(input, config) {
        var options = Object.assign({}, defaults, config || {});
        var previous = input[DATA_KEY];

        if (previous) {
            input.removeEventListener('input', previous.handleInput);
            input.removeEventListener('change', previous.handleChange);
            input.removeEventListener('click', previous.handleClick);
        }

        var rawValue = input.value || input.getAttribute('value') || input.getAttribute('data-time') || '';
        var parsed = parseTime(rawValue);
        var includeSeconds = /ss/.test(options.format);
        var nativeSupported = supportsNativeTimeInput();

        try {
            input.type = nativeSupported ? 'time' : 'text';
        } catch (e) {
            input.setAttribute('type', nativeSupported ? 'time' : 'text');
        }

        input.setAttribute('data-skm-time-input', nativeSupported ? 'native' : 'text-fallback');
        input.setAttribute('inputmode', 'numeric');
        input.step = includeSeconds ? '1' : '60';

        if (!nativeSupported) {
            input.setAttribute('pattern', '\\d{1,2}:\\d{2}(:\\d{2})?');
        }

        if (options.readOnly) {
            input.readOnly = false;
            input.setAttribute('data-skm-time-readonly-compat', '1');
        }

        if (parsed) {
            input.value = nativeValue(parsed, includeSeconds);
            updateDataAttributes(input, options, false);
        }

        var state = {
            handleInput: function () {
                updateDataAttributes(input, options, false);
            },
            handleChange: function () {
                updateDataAttributes(input, options, true);
            },
            handleClick: function () {
                input.focus();

                if (typeof input.showPicker === 'function') {
                    try {
                        input.showPicker();
                    } catch (e) {
                        // Browsers only allow showPicker during trusted user activation.
                    }
                }
            },
        };

        input.addEventListener('input', state.handleInput);
        input.addEventListener('change', state.handleChange);
        input.addEventListener('click', state.handleClick);
        input[DATA_KEY] = state;
    }

    function destroyInput(input) {
        var previous = input[DATA_KEY];
        if (!previous) return;

        input.removeEventListener('input', previous.handleInput);
        input.removeEventListener('change', previous.handleChange);
        input.removeEventListener('click', previous.handleClick);
        delete input[DATA_KEY];
    }

    function resolveNodes(target) {
        if (!target) return [];
        if (typeof target === 'string') {
            return root.document ? root.document.querySelectorAll(target) : [];
        }
        if (target.nodeType) return [target];
        return target.length !== undefined ? target : [target];
    }

    root.skmTimeInput = {
        defaults: defaults,
        init: function (target, config) {
            var nodes = resolveNodes(target);
            Array.prototype.forEach.call(nodes, function (node) {
                if (node) initInput(node, config);
            });
        },
        destroy: destroyInput,
        parseTime: parseTime,
        formatTime: formatTime,
    };
})(window);
